import { useCallback, useEffect, useState, type ReactNode } from 'react'
import {
  deleteFeature,
  getMission,
  missionFeatures,
  missionPositions,
  missionReports,
  missionRoster,
  missionTeams,
  moveTeamToFaction,
  createFeature,
  deleteReport,
  setMissionRole,
  setReportStatus,
  updateFeature,
  withdrawReport,
  updateMission,
} from '../api/endpoints'
import type {
  DroneFeed,
  User,
  MapFeature,
  Mission,
  MissionRole,
  MissionTeam,
  Organisation,
  Participant,
  Position,
  TacticalReport,
} from '../api/types'
import { TocMap } from '../components/TocMap'
import { ChatPanel } from '../components/ChatPanel'
import { MapFilePanel } from '../components/MapFilePanel'
import { FeatureEditor } from '../components/FeatureEditor'
import { DraftBar, DrawSaveModal, DrawToolbar, type DrawDetails } from '../components/DrawTools'
import { VERTEX_LIMIT, type DrawTool } from '../import/draw'
import { ReportPanel } from '../components/ReportPanel'
import { DroneFeedsSection, DroneVideoPanel } from '../components/DroneFeeds'
import { REPORT_NAMES, isOpen as reportIsOpen, reportPosition } from '../import/reports'
import { distance, type LatLng } from '../import/geo'
import { editsOf, kindName, saveBody, type FeatureEdits } from '../import/featureEdit'
import { JoinPanel } from '../components/JoinPanel'
import { StatusPill } from '../components/StatusPill'
import { Modal } from '../components/Modal'
import { Activity, Flag, PanelLeft, Plus } from '../components/Icons'

/**
 * The tactical operations centre for one event.
 *
 * What a TOC is for: seeing the whole picture at once and changing the parts of it you are
 * responsible for. Everything here is either something an organiser watches — who is on the ground,
 * what has been reported, where everyone is — or something only an organiser can do: open the
 * event, move a team between sides, place an objective.
 *
 * It polls rather than holding a socket. The realtime channels are scoped per faction by design,
 * and an organiser needs every faction at once; polling the snapshot endpoints gets that without
 * teaching the transport a new kind of subscriber that can see across the whole game.
 */
export function Toc({
  mission: initial,
  organisation,
  me,
  header,
  onClose,
}: {
  mission: Mission
  organisation: Organisation
  me: User
  /** The shared header, given this page's breadcrumb and actions. */
  header: (crumbs?: ReactNode, actions?: ReactNode) => ReactNode
  onClose: () => void
}) {
  const [mission, setMission] = useState(initial)
  const [teams, setTeams] = useState<MissionTeam[]>([])
  const [roster, setRoster] = useState<Participant[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [features, setFeatures] = useState<MapFeature[]>([])
  const [reports, setReports] = useState<TacticalReport[]>([])
  const [error, setError] = useState<string | null>(null)

  // The feature open in the editor, and the working copy of it. Kept apart from `features`, which
  // the poll replaces every second and a half: editing the polled copy would lose every keystroke.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [edits, setEdits] = useState<FeatureEdits | null>(null)
  const [saving, setSaving] = useState(false)

  // Drawers, used only when the window is too narrow for all three columns.
  const [drawer, setDrawer] = useState<'plan' | 'live' | null>(null)

  // Drawing: which tool, the points so far, and whether it started as "place an objective".
  const [tool, setTool] = useState<DrawTool | null>(null)
  const [points, setPoints] = useState<LatLng[]>([])
  const [objectivePreset, setObjectivePreset] = useState(false)
  const [naming, setNaming] = useState(false)

  const [reportId, setReportId] = useState<string | null>(null)
  const [focus, setFocus] = useState<{ at: LatLng; key: number } | null>(null)
  const [confirmEnd, setConfirmEnd] = useState(false)
  // The drone feed being watched over the map, if any.
  const [watching, setWatching] = useState<DroneFeed | null>(null)
  const [updatedAt, setUpdatedAt] = useState<number | null>(null)

  const canRun = organisation.myRole === 'OWNER' || organisation.myRole === 'MANAGER'

  /// What moves during a game, polled hard: where people are, what has been drawn, what has been
  /// reported. These are the only three worth a fast tick, and separating them is what lets the
  /// tick be fast without tripling the traffic.
  const refreshLive = useCallback(async () => {
    const [p, f, rep] = await Promise.all([
      missionPositions(mission.id),
      missionFeatures(mission.id),
      missionReports(mission.id),
    ])

    if (p.ok) setPositions(p.value)
    if (f.ok) setFeatures(f.value)
    if (rep.ok) setReports(rep.value)
    if (p.ok && f.ok && rep.ok) setUpdatedAt(Date.now())

    // Only the first failure is shown. Several banners saying the same thing about one outage is
    // worse than one, and the map keeps showing the last good picture regardless.
    const failed = [p, f, rep].find((result) => !result.ok)
    setError(failed && !failed.ok ? failed.error.message : null)
  }, [mission.id])

  /// What changes when somebody joins or an organiser acts. Minutes apart, not seconds.
  const refreshRoster = useCallback(async () => {
    const [m, t, r] = await Promise.all([
      getMission(mission.id),
      missionTeams(mission.id),
      missionRoster(mission.id),
    ])

    if (m.ok) setMission(m.value)
    if (t.ok) setTeams(t.value)
    if (r.ok) setRoster(r.value)
  }, [mission.id])

  // The organisation's managers host its events, so this is true for almost everybody on the
  // dashboard; a staff member watching the TOC gets the read-only roster.
  const isHost = mission.myMissionRole === 'HOST'

  /// Everything, for the first paint and after anything this dashboard did itself.
  const refresh = useCallback(async () => {
    await Promise.all([refreshLive(), refreshRoster()])
  }, [refreshLive, refreshRoster])

  useEffect(() => {
    void refresh()

    // A hidden tab is not watched. Browsers throttle its timers anyway, so the requests buy
    // nothing and a marshal with the TOC behind a browser window was costing the same as one
    // watching it. Coming back refreshes at once rather than waiting out a tick.
    const awake = () => document.visibilityState === 'visible'

    const live = setInterval(() => {
      if (awake()) void refreshLive()
    }, 1500)
    const slow = setInterval(() => {
      if (awake()) void refreshRoster()
    }, 15000)

    const onVisible = () => {
      if (awake()) void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(live)
      clearInterval(slow)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refresh, refreshLive, refreshRoster])

  const selected = features.find((feature) => feature.id === selectedId) ?? null

  // Deleted elsewhere while open: `selected` becomes null and the editor simply is not drawn.

  function select(featureId: string | null) {
    if (tool) return
    setReportId(null)
    const feature = features.find((entry) => entry.id === featureId) ?? null
    setSelectedId(feature?.id ?? null)
    setEdits(feature ? editsOf(feature) : null)
  }

  async function saveSelected() {
    if (!selected || !edits) return
    setSaving(true)
    const result = await updateFeature(selected.id, saveBody(selected, edits))
    setSaving(false)
    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setFeatures((current) => current.map((f) => (f.id === result.value.id ? result.value : f)))
    setEdits(editsOf(result.value))
  }

  async function deleteSelected() {
    if (!selected) return
    const result = await deleteFeature(selected.id)
    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setFeatures((current) => current.filter((f) => f.id !== selected.id))
    setSelectedId(null)
    setEdits(null)
  }

  function startDrawing(next: DrawTool | null, asObjective = false) {
    select(null)
    setReportId(null)
    setTool(next)
    setPoints([])
    setObjectivePreset(asObjective)
  }

  function stopDrawing() {
    setTool(null)
    setPoints([])
    setNaming(false)
    setObjectivePreset(false)
  }

  function addPoint(point: LatLng) {
    if (!tool || naming) return
    const limit = VERTEX_LIMIT[tool]
    if (limit !== undefined && points.length >= limit) return
    const next = [...points, point]
    setPoints(next)
    // A marker is finished the moment it is placed.
    if (tool === 'marker') setNaming(true)
  }

  async function saveDrawing(details: DrawDetails) {
    if (!tool) return
    const at = (p: LatLng) => ({ latitude: p[0], longitude: p[1] })
    const geometry =
      tool === 'marker'
        ? { geometryType: 'POINT', positions: [at(points[0])] }
        : tool === 'route'
          ? { geometryType: 'POLYLINE', positions: points.map(at) }
          : tool === 'area'
            ? { geometryType: 'POLYGON', positions: points.map(at) }
            : tool === 'box'
              ? { geometryType: 'RECTANGLE', positions: points.map(at) }
              : {
                  geometryType: 'CIRCLE',
                  positions: [at(points[0])],
                  radiusMetres: Math.max(1, distance(points[0], points[1])),
                }
    const everyone = details.audience === 'MISSION'
    const result = await createFeature(mission.id, {
      ...geometry,
      visibility: everyone ? 'MISSION' : 'FACTION',
      factionKey: everyone ? undefined : details.audience,
      affiliation: details.affiliation,
      symbol: details.objective && tool === 'marker' ? 'OBJECTIVE' : undefined,
      objective: details.objective,
      label: details.label.trim() || undefined,
      remarks: details.remarks.trim() || undefined,
      colorHex: details.colorHex ?? undefined,
      clientRequestId: `toc-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    })
    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setFeatures((current) => [...current, result.value])
    stopDrawing()
  }

  const report = reports.find((entry) => entry.id === reportId) ?? null

  async function reportAction(action: () => Promise<{ ok: boolean; error?: { message: string } }>) {
    const result = await action()
    if (!result.ok) setError(result.error?.message ?? 'That did not work.')
    await refreshLive()
  }

  const objectives = features.filter((feature) => feature.objective)
  const openReports = reports.filter(reportIsOpen)

  async function setStatus(status: Mission['status']) {
    const result = await updateMission(mission.id, { status })
    if (result.ok) setMission(result.value)
    else setError(result.error.message)
  }

  const crumbs = (
    <>
      <button className="crumb" onClick={onClose}>
        {organisation.name}
      </button>
      <span className="sep" aria-hidden>
        /
      </span>
      <span className="current">{mission.name}</span>
      <StatusPill status={mission.status} />
      {mission.locationName && <span className="tertiary hide-narrow">{mission.locationName}</span>}
    </>
  )

  const actions = (
    <>
      <LiveIndicator updatedAt={updatedAt} failing={error !== null} />
      <button
        className="secondary drawer-toggle plan with-icon"
        aria-expanded={drawer === 'plan'}
        onClick={() => setDrawer((d) => (d === 'plan' ? null : 'plan'))}
      >
        <PanelLeft /> Plan
      </button>
      <button
        className="secondary drawer-toggle live with-icon"
        aria-expanded={drawer === 'live'}
        onClick={() => setDrawer((d) => (d === 'live' ? null : 'live'))}
      >
        <Activity /> Live
        {openReports.length > 0 && <span className="badge alert">{openReports.length}</span>}
      </button>
      {canRun && mission.status === 'PLANNING' && (
        <button onClick={() => void setStatus('ACTIVE')}>Start event</button>
      )}
      {canRun && mission.status === 'ACTIVE' && (
        <button className="secondary" onClick={() => setConfirmEnd(true)}>
          End event
        </button>
      )}
    </>
  )

  return (
    <div className="toc">
      {header(crumbs, actions)}

      {error && (
        <div className="banner danger" style={{ margin: '8px 16px 0' }}>
          {error}
        </div>
      )}

      <div className={`toc-body${drawer ? ` show-${drawer}` : ''}`}>
        {/* The plan: what the event is about, who is on which side, and the setup. */}
        <aside className="toc-panel toc-plan" aria-label="Plan">
          <section className="panel-section">
            <div className="panel-head">
              <h3>
                Objectives <span className="badge">{objectives.length}</span>
              </h3>
              {canRun && (
                <button
                  className={tool === 'marker' && objectivePreset ? 'secondary' : 'with-icon'}
                  onClick={() =>
                    tool === 'marker' && objectivePreset ? stopDrawing() : startDrawing('marker', true)
                  }
                >
                  {tool === 'marker' && objectivePreset ? (
                    'Cancel'
                  ) : (
                    <>
                      <Plus /> Place
                    </>
                  )}
                </button>
              )}
            </div>

            {objectives.length === 0 && (
              <p className="tertiary" style={{ margin: 0 }}>
                {canRun ? 'Place one on the map for everybody to see.' : 'None placed yet.'}
              </p>
            )}

            {/* Opens the same editor as clicking it on the map: one place to change or remove
                anything, with one confirmation, instead of an unconfirmed cross in a list. */}
            {objectives.map((objective) => (
              <button
                key={objective.id}
                className={`list-row${objective.id === selectedId ? ' selected' : ''}`}
                onClick={() => select(objective.id)}
              >
                <span className="row" style={{ gap: 8 }}>
                  <Flag /> {objective.label ?? kindName(objective)}
                </span>
                <span className="tertiary" aria-hidden>
                  ›
                </span>
              </button>
            ))}
          </section>

          <section className="panel-section">
            <div className="panel-head">
              <h3>Sides</h3>
            </div>
            {mission.factions.map((faction) => {
              const inFaction = teams.filter((team) => team.factionKey === faction.key)
              // People fighting for this side, which is not everybody carrying its faction key.
              // Whoever created the event is a participant with no team, and counting them here
              // made a side with one squad on it report two players.
              const players = roster.filter(
                (person) => person.factionKey === faction.key && person.teamId,
              )

              return (
                <div key={faction.key} className="side-card">
                  <div className="spread">
                    <strong>{faction.name}</strong>
                    <span className="tertiary">
                      {inFaction.length} team{inFaction.length === 1 ? '' : 's'} · {players.length}{' '}
                      player{players.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  {inFaction.length === 0 && (
                    <p className="tertiary" style={{ margin: '6px 0 0' }}>
                      No team yet.
                    </p>
                  )}

                  {inFaction.map((team) => (
                    <div key={team.teamId} className="team-row">
                      <div className="row" style={{ gap: 8, minWidth: 0 }}>
                        <span
                          className="swatch-dot"
                          aria-hidden
                          style={{ background: team.colorHex ?? 'var(--text-tertiary)' }}
                        />
                        <span title={team.teamName}>{team.teamTag ?? team.teamName}</span>
                      </div>

                      {canRun && mission.factions.length > 1 && (
                        <select
                          aria-label={`Side for ${team.teamName}`}
                          value={team.factionKey}
                          onChange={async (event) => {
                            const result = await moveTeamToFaction(
                              mission.id,
                              team.teamId,
                              event.target.value,
                            )
                            if (result.ok) await refresh()
                            else setError(result.error.message)
                          }}
                        >
                          {mission.factions.map((option) => (
                            <option key={option.key} value={option.key}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              )
            })}
          </section>

          {/* Set up before the game and rarely touched during it, so folded away once it starts. */}
          {canRun && mission.joinCode && (
            <details className="disclosure" open={mission.status === 'PLANNING'}>
              <summary>
                <span className="row" style={{ gap: 10 }}>
                  Join code <span className="mono tertiary">{mission.joinCode}</span>
                </span>
              </summary>
              <div className="disclosure-body">
                <JoinPanel mission={mission} />
              </div>
            </details>
          )}

          <details className="disclosure">
            <summary>Map files</summary>
            <div className="disclosure-body">
              <MapFilePanel mission={mission} onImported={() => void refresh()} />
            </div>
          </details>
        </aside>

        <main className="toc-map" aria-label="Map">
          <TocMap
            mission={mission}
            teams={teams}
            roster={roster}
            positions={positions}
            features={features}
            placing={false}
            onPick={() => {}}
            selectedId={selectedId}
            edits={edits}
            editable={isHost}
            onSelect={(id) => {
              setDrawer(null)
              select(id)
            }}
            onReshape={(positions) =>
              setEdits((current) => (current ? { ...current, positions } : current))
            }
            drawing={tool ? { tool, points } : null}
            onDrawClick={addPoint}
            focus={focus}
          />
          {isHost && (
            <DrawToolbar
              active={tool}
              onPick={(next) => (next ? startDrawing(next) : stopDrawing())}
            />
          )}
          {tool && !naming && (
            <DraftBar
              tool={tool}
              points={points}
              onUndo={() => setPoints((current) => current.slice(0, -1))}
              onDiscard={stopDrawing}
              onSave={() => setNaming(true)}
            />
          )}
          {report && (
            <ReportPanel
              report={report}
              teams={teams}
              roster={roster}
              isHost={isHost}
              onStatus={(status) => reportAction(() => setReportStatus(report.id, status))}
              onWithdraw={() => reportAction(() => withdrawReport(report.id))}
              onDelete={async () => {
                await reportAction(() => deleteReport(report.id))
                setReportId(null)
                await refreshLive()
              }}
              onShowOnMap={() => {
                const at = reportPosition(report)
                if (at) setFocus({ at, key: Date.now() })
              }}
              onClose={() => setReportId(null)}
            />
          )}
          {selected && edits && (
            <FeatureEditor
              feature={selected}
              edits={edits}
              editable={isHost}
              saving={saving}
              teams={teams}
              roster={roster}
              onChange={setEdits}
              onSave={() => void saveSelected()}
              onRevert={() => setEdits(editsOf(selected))}
              onDelete={deleteSelected}
              onClose={() => select(null)}
            />
          )}
          {isHost && watching && (
            <DroneVideoPanel
              // A new feed is a new player, from "connecting".
              key={watching.id}
              missionId={mission.id}
              feed={watching}
              onClose={() => setWatching(null)}
            />
          )}
        </main>

        {/* What is happening now, most urgent first: casualties, then people, then talk. */}
        <aside className="toc-panel toc-live" aria-label="Live">
          <section className="panel-section">
            <div className="panel-head">
              <h3>
                Reports
                {openReports.length > 0 ? (
                  <span className="badge alert">{openReports.length} open</span>
                ) : (
                  <span className="badge">{reports.length}</span>
                )}
              </h3>
            </div>

            {reports.length === 0 && (
              <p className="tertiary" style={{ margin: 0 }}>
                Nothing reported. Casualty and contact reports from the field appear here.
              </p>
            )}

            {reports.slice(0, 20).map((report) => {
              const isOpen = reportIsOpen(report)
              return (
                <button
                  key={report.id}
                  className={`report-row${isOpen ? ' open' : ''}${report.id === reportId ? ' selected' : ''}`}
                  onClick={() => {
                    select(null)
                    setReportId(report.id)
                    setDrawer(null)
                    const at = reportPosition(report)
                    if (at) setFocus({ at, key: Date.now() })
                  }}
                >
                  <div className="spread">
                    <strong style={{ fontSize: 13 }}>{report.title ?? REPORT_NAMES[report.type]}</strong>
                    <span className={`pill ${isOpen ? 'danger' : 'neutral'}`}>
                      {report.status.toLowerCase().replace('_', ' ')}
                    </span>
                  </div>
                  <div className="tertiary" style={{ marginTop: 4 }}>
                    {teamName(teams, report.teamId)}
                    {report.createdAt ? ` · ${timeAgo(report.createdAt)}` : ''}
                  </div>
                </button>
              )
            })}
          </section>

          {/* The drone sees both sides, so its feeds are for hosts and nobody else. */}
          {isHost && (
            <section className="panel-section">
              <DroneFeedsSection
                missionId={mission.id}
                watchingId={watching?.id ?? null}
                onWatch={setWatching}
              />
            </section>
          )}

          <section className="panel-section">
            <div className="panel-head">
              <h3>
                On the ground <span className="badge">{roster.length}</span>
              </h3>
            </div>
            {roster.length === 0 && (
              <p className="tertiary" style={{ margin: 0 }}>
                Nobody has joined yet.
              </p>
            )}

            {roster.map((person) => {
              const position = positions.find((p) => p.userId === person.userId)
              const fix = !person.sharePosition
                ? { kind: 'none', text: 'hidden' }
                : !position
                  ? { kind: 'none', text: 'no fix' }
                  : position.stale
                    ? { kind: 'stale', text: 'last known' }
                    : { kind: 'live', text: 'live' }
              return (
                <div key={person.userId} className="person-row">
                  <div className="who">
                    <span
                      className="swatch-dot"
                      aria-hidden
                      style={{ background: person.teamColorHex ?? 'var(--text-tertiary)' }}
                    />
                    <span className="name">{person.callsign}</span>
                  </div>
                  {isHost ? (
                    // Hosts appoint other hosts and marshals here. Every host has the same
                    // powers; the server refuses to remove the last one, and says so.
                    <select
                      aria-label={`Role of ${person.callsign}`}
                      value={person.missionRole}
                      onChange={async (event) => {
                        const result = await setMissionRole(
                          mission.id,
                          person.userId,
                          event.target.value as MissionRole,
                        )
                        if (result.ok) await refresh()
                        else setError(result.error.message)
                      }}
                    >
                      <option value="HOST">Host</option>
                      <option value="MARSHAL">Marshal</option>
                      <option value="PARTICIPANT">Player</option>
                    </select>
                  ) : (
                    person.missionRole !== 'PARTICIPANT' && (
                      <span className="pill neutral">{person.missionRole.toLowerCase()}</span>
                    )
                  )}
                  <span className={`fix ${fix.kind}`}>{fix.text}</span>
                </div>
              )
            })}
          </section>

          <section className="panel-section grow">
            <div className="panel-head">
              <h3>Messages</h3>
            </div>
            <ChatPanel missionId={mission.id} roster={roster} meId={me.id} isHost={isHost} />
          </section>
        </aside>
      </div>

      {confirmEnd && (
        <Modal title={`End ${mission.name}?`} onClose={() => setConfirmEnd(false)}>
          <div className="stack">
            <p style={{ margin: 0 }}>
              Live positions are cleared and no more teams can join. The map, markers, messages and
              reports are kept for the debrief.
            </p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="secondary" onClick={() => setConfirmEnd(false)}>
                Keep running
              </button>
              <button
                className="danger"
                onClick={async () => {
                  setConfirmEnd(false)
                  await setStatus('COMPLETED')
                }}
              >
                End event
              </button>
            </div>
          </div>
        </Modal>
      )}

      {naming && tool && tool !== 'measure' && (
        <DrawSaveModal
          tool={tool}
          factions={mission.factions}
          objective={objectivePreset}
          onCancel={() => {
            setNaming(false)
            // A marker has nothing to go back to; a shape keeps its points to carry on with.
            if (tool === 'marker') setPoints([])
          }}
          onSave={saveDrawing}
        />
      )}
    </div>
  )
}

/**
 * How fresh the picture is. A TOC that has silently stopped updating looks exactly like a quiet
 * game, so the age of the data is always on screen.
 */
function LiveIndicator({ updatedAt, failing }: { updatedAt: number | null; failing: boolean }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [])

  const age = updatedAt === null ? null : Math.max(0, Math.round((now - updatedAt) / 1000))
  const stale = failing || age === null || age > 10
  const text = failing
    ? 'Not updating'
    : age === null
      ? 'Connecting'
      : age <= 3
        ? 'Live'
        : `Updated ${age}s ago`
  return (
    <span className="live-indicator hide-narrow" role="status">
      <span className={`fix ${stale ? 'stale' : 'live'}`}>{text}</span>
    </span>
  )
}


function teamName(teams: MissionTeam[], teamId?: string): string {
  if (!teamId) return 'unknown team'
  return teams.find((team) => team.teamId === teamId)?.teamName ?? 'unknown team'
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`
  return `${Math.floor(seconds / 86400)} d ago`
}
